import { GpsState, GyroService, IntroFlowService, MapService, MoteEventService, PositionService } from "../../providers"
import { Media, MediaType, Mote, MoteEventType, MoteSortType, MotesPaths, MoteType, User } from "../../models"

// Page key (identifies a page)
export enum PageKey {
    welcomePage = 'welcomePage',
    cookiePage = 'cookiePage',
    loginPage = 'loginPage',
    locationPage = 'locationPage',
    directionPage = 'directionPage',
    groupPresentationPage = 'groupPresentationPage',
    groupPage = 'groupPage',
    challengePage = 'challengePage',
    trailerPage = 'trailerPage',
    mapUserPage = "mapUserPage",
    mapStoryPage = "mapStoryPage",
    mapNavigationPage = "mapNavigationPage",
    tutorialPage = "tutorialPage"
};

export interface PageLink {
    description: string,
    action: Action
}

export interface PageState {
    actions: Action[],
    loadingMessage: string,
    disableButtons: boolean
}

export interface PageIcon {
    name: string,
    animate: boolean
}

export enum PagePosition {
    active = 'active',            // Animate to middle
    left = 'left',                // Animate to left
    right = 'right',              // Animate to right
    place_left = 'place_left',    // Place left
    place_right = 'place_right',  // Place right
    place_active = 'place_active' // Place middle
}

// Page content (is included in a page)
export abstract class Page {
    public key: PageKey;
    public header: string = '';
    public icon: PageIcon = null;
    public tourTitle: string = '';
    public tourPreview: string = '';
    public description: string = '';
    public links: PageLink[] = [];
    public state: PageState = { actions: [], loadingMessage: '', disableButtons: false };
    public media: Media = null;
    public mediaType: MediaType = null;
    public position: PagePosition = PagePosition.place_right;
    public groupPage: boolean = false;
    public mapPage: boolean = false;
    public animationPage: boolean = false;
    public abstract setCookie(tour: Mote): Promise<void>;
    public abstract show(tour: Mote, user: User, tourAccess: boolean): Promise<boolean>;
    public abstract updatePage(page: Page, tour: Mote, user: User, gpsState: GpsState, activeIcon: string, gpsHelpComplete: boolean, firstMote: Mote, tourAccess: boolean, autoUpdateState: boolean): Promise<void>;
};

// Text convert helper functions
function capitalize(string): string {
    return string.charAt(0).toUpperCase() + string.slice(1);
}

function getStoryPosition(tour: Mote): string {
    return (tour.sortBy == MoteSortType.DISTANCE) ? $localize`closest` : (tour.sortBy == MoteSortType.START_DATE ? $localize`next` : $localize`first`);
}

function getStoryType(tour: Mote): string {
    return (tour.type == MoteType.SCHEDULE) ? $localize`event` : (tour.type == MoteType.QUEST ? $localize`quest` : $localize`story`);
}

function raceTimeout<T>(promise: Promise<T>, timeoutMs: number = 9000) : Promise<T>{
    return Promise.race([promise, new Promise<null>( resolve => setTimeout( () => resolve(null), timeoutMs))]);
}

// Page button action
export enum Action {
    WELCOME_SEEN = "WELCOME_SEEN",
    ALLOW_COOKIE = "ALLOW_COOKIE",
    OPEN_TERMS = "OPEN_TERMS",
    OPEN_COOKIES = "OPEN_COOKIES",
    OPEN_LICENSE = "OPEN_LICENSE",
    OPEN_CONTACT = "OPEN_CONTACT",
    LOGIN = "LOGIN",
    CONFIRM_LOGIN = "CONFIRM_LOGIN",
    LOGOUT = "LOGOUT",
    FIND_ANOTHER_TOUR = "FIND_ANOTHER_TOUR",
    BUY_TOUR = "BUY_TOUR",
    ALLOW_LOCATION = "ALLOW_LOCATION",
    HELP_ME_LOCATION = "HELP_ME_LOCATION",
    CONTINUE_WITHOUT_LOCATION = "CONTINUE_WITHOUT_LOCATION",
    RELOAD = "RELOAD",
    ALLOW_DIRECTION = "ALLOW_DIRECTION",
    NO_GROUP = "NO_GROUP",
    GROUP_TOUR = "GROUP_TOUR",
    NEXT = "NEXT",
    CONTINUE = "CONTINUE",
    CHALLENGE_SEEN = "CHALLENGE_SEEN",
    START_TOUR = "START_TOUR",
    EXPLORE_TOURS = "EXPLORE_TOURS",
    MAP_USER_SEEN = "MAP_USER_SEEN",
    MAP_STORY_SEEN = "MAP_STORY_SEEN",
    MAP_NAV_SEEN = "MAP_NAV_SEEN",
    START_TUTORIAL = "START_TUTORIAL",
    USER_ANIMATION_SEEN = "USER_ANIMATION_SEEN",
    STORY_ANIMATION_SEEN = "STORY_ANIMATION_SEEN",
    UNLOCK_ANIMATION_SEEN = "UNLOCK_ANIMATION_SEEN",
    TOUR_ANIMATION_SEEN = "TOUR_ANIMATION_SEEN",
    ROUTE_ANIMATION_SEEN = "ROUTE_ANIMATION_SEEN",
    COLLECTION_ANIMATION_SEEN = "COLLECTION_ANIMATION_SEEN",
    BROWSE_TOURS = "BROWSE_TOURS"
};

// Page button action labels
export const ActionLabel: { [action in Action]: string } = {
    WELCOME_SEEN: $localize`Start`,
    ALLOW_COOKIE: $localize`I agree`,
    LOGIN: $localize`Sign in / create user`,
    CONFIRM_LOGIN: $localize`That's me`,
    FIND_ANOTHER_TOUR: $localize`Find another tour`,
    BUY_TOUR: $localize`Buy tour now`,
    OPEN_TERMS: $localize`Terms of User`,
    OPEN_COOKIES: $localize`Cookie Policy`,
    OPEN_LICENSE: $localize`Open Source Licensing`,
    OPEN_CONTACT: MotesPaths.SUPPORT_EMAIL,
    LOGOUT: $localize`Sign out`,
    ALLOW_LOCATION: $localize`Continue`,
    HELP_ME_LOCATION: $localize`Help me`,
    CONTINUE_WITHOUT_LOCATION: $localize`Continue without`,
    RELOAD: $localize`Reload page to try again`,
    ALLOW_DIRECTION: $localize`Allow direction`,
    NEXT: $localize`Next`,
    CONTINUE: $localize`Continue`,
    NO_GROUP: $localize`Not now`,
    GROUP_TOUR: $localize`With group`,
    CHALLENGE_SEEN: $localize`Sounds cool`,
    START_TOUR: $localize`Start tour`,
    EXPLORE_TOURS: $localize`Explore tours`,
    MAP_USER_SEEN: $localize`Got it`,
    MAP_STORY_SEEN: $localize`Take me there`,
    MAP_NAV_SEEN: $localize`I'm ready`,
    START_TUTORIAL: $localize`Start introduction`,
    USER_ANIMATION_SEEN: $localize`Next`,
    STORY_ANIMATION_SEEN: $localize`Sounds exiting`,
    UNLOCK_ANIMATION_SEEN: $localize`Awesome`,
    TOUR_ANIMATION_SEEN: $localize`Sounds nice`,
    ROUTE_ANIMATION_SEEN: $localize`I see`,
    COLLECTION_ANIMATION_SEEN: $localize`Next`,
    BROWSE_TOURS: $localize`Start browsing tours`
}

export class WelcomePage extends Page {
    public readonly key = PageKey.welcomePage;
    public state = { actions: [Action.WELCOME_SEEN], loadingMessage: '', disableButtons: false };
    public mediaType = MediaType.IMAGE;

    constructor(private introFlowService: IntroFlowService) {
        super();
    }

    public async setCookie(tour: Mote) {
        await this.introFlowService.setLocalStorage(MotesPaths.LOCAL_STORAGE_WELCOME_SEEN(tour.key))
    }

    public async show(tour: Mote) {
        // Show if tour is specified and user hasn't seen this page before
        if(!tour) return false;
        return tour.project != MotesPaths.DEFAULT_PROJECT_KEY && !(await this.introFlowService.isLocalStorageSet(MotesPaths.LOCAL_STORAGE_WELCOME_SEEN(tour.key)))
    }

    public async updatePage(page: Page, tour: Mote) {
        page.media = tour?.image;
        if(page.media) page.mediaType = MediaType.IMAGE;
        page.tourPreview = tour?.previewText;
        page.tourTitle = tour?.title;
    }
}

export class CookiePage extends Page {
    public readonly key = PageKey.cookiePage;
    public header = $localize`Cookies`;
    public icon = { name: 'intro-flow-cookies', animate: false };
    public links = [{ description: $localize`By using our service you agree to our`, action: Action.OPEN_TERMS }, { description: $localize`and are informed about our`, action: Action.OPEN_COOKIES }, { description: $localize`and usage of`, action: Action.OPEN_LICENSE }];
    public state = { actions: [Action.ALLOW_COOKIE], loadingMessage: '', disableButtons: false };

    constructor(private introFlowService: IntroFlowService) {
        super();
    }

    public async setCookie() {
        return this.introFlowService.setLocalStorage(MotesPaths.LOCAL_STORAGE_COOKIES_ACCEPTED);
    }

    public async show() {
        // Show if user hasn't seen this page before
        return !(await this.introFlowService.isLocalStorageSet(MotesPaths.LOCAL_STORAGE_COOKIES_ACCEPTED));
    }

    public updatePage() { return null }
}

export class LoginPage extends Page {
    public readonly key = PageKey.loginPage;
    public header = $localize`Sign In`;
    public icon = { name: 'intro-flow-login', animate: false };
    public description = $localize`Sign in to start your tour.`;
    public state = { actions: [Action.LOGIN], loadingMessage: '', disableButtons: false };

    public setCookie() { return null }

    public async show(tour: Mote, user: User, tourAccess: boolean) {
        // Show if tour is specified, costs money and user is not logged in, doesn't own the voucher, or if voucher is invalid
        return tour && tour.project != MotesPaths.DEFAULT_PROJECT_KEY && tour.price && (!user || user.isAnonymous || !tourAccess);
    }

    public async updatePage(page: Page, tour, user: User, gpsState: GpsState, activeIcon: string, gpsHelpComplete: boolean, firstMote: Mote, tourAccess: boolean) {
        // If user is not logged in
        if(!user || user.isAnonymous) {
            page.icon = { name: 'intro-flow-login', animate: false };
            page.description = $localize`Sign in to start your tour.`;
            page.links = [];
            page.state = { actions: [Action.LOGIN], loadingMessage: '', disableButtons: false };
        }
        // If user does not own the voucher
        else if(!tourAccess) {
            page.icon = { name: 'intro-flow-location-denied', animate: false };
            page.description = $localize`This tour doesn't seem to belong to you. Maybe you bought this tour with a different user? Try signing in with a different user.`;
            page.links = [{ description: $localize`If the problem persists, contact customer support on`, action: Action.OPEN_CONTACT }];
            page.state = { actions: [Action.LOGOUT, Action.FIND_ANOTHER_TOUR, Action.BUY_TOUR], loadingMessage: user?.userName ? $localize`Hello there ${user.userName}! Something went wrong.` : '', disableButtons: false };
        }
        // Else => proceed
        else {
            page.icon = { name: 'intro-flow-login', animate: false };
            page.description = '';
            page.links = [];
            page.state = { actions: [Action.LOGOUT, Action.CONFIRM_LOGIN], loadingMessage: user?.userName ? $localize`Welcome ${user.userName}` : '', disableButtons: false };
        }
    }
}

export class LocationPage extends Page {
    public readonly key = PageKey.locationPage;
    public header = $localize`Allow Location`;
    public icon = { name: 'intro-flow-location', animate: false };
    public description = $localize`To unlock stories you have to be close to them. Therefore the app works best with access to your location.`;
    public state = { actions: [Action.ALLOW_LOCATION], loadingMessage: '', disableButtons: false };

    constructor(private introFlowService: IntroFlowService, private positionService: PositionService) {
        super();
    }

    public setCookie() { return null }

    public async show(tour: Mote) {
        if(!tour) return false;
        const gpsPermission = await this.positionService.getGpsPermissions();
        if(gpsPermission == 'granted') {
            this.positionService.init();
            return false;
        }

        // Do not show if location is ignored for this tour
        if(await this.introFlowService.isLocalStorageSet(MotesPaths.LOCAL_STORAGE_LOCATION_IGNORED(tour.key))) return false;
        // Show if tour is specified and GPS signal has not yet been found
        let gpsState = this.positionService.currentState();
        return tour.project != MotesPaths.DEFAULT_PROJECT_KEY && gpsState != GpsState.ACCESS_ALLOWED && gpsState != GpsState.FIRST_VALID && gpsState != GpsState.POS_VALID;
    }

    public async updatePage(page: Page, tour: Mote, user: User, gpsState: GpsState, activeIcon: string, gpsHelpComplete: boolean) {
        // Set icon, button and loading message look and functionality according to gps state
        switch(gpsState) {
            case GpsState.SERVICE_INITIALIZED:
            case GpsState.ACCESS_ALLOWED:
                page.icon.animate = true;
                page.state = { actions: [Action.ALLOW_LOCATION], loadingMessage: $localize`Finding your location...`, disableButtons: true };
                break;
            case GpsState.ACCESS_DENIED:
                page.icon = { name: 'intro-flow-location-denied', animate: false };
                if(gpsHelpComplete) page.state = { actions: [Action.RELOAD, Action.CONTINUE_WITHOUT_LOCATION], loadingMessage: $localize`Ready again to look for your location...`, disableButtons: false };
                else page.state = { actions: [Action.HELP_ME_LOCATION, Action.CONTINUE_WITHOUT_LOCATION], loadingMessage: $localize`Access to your location seems to be denied - by you?`, disableButtons: false };
                break;
            case GpsState.POS_VALID:
            case GpsState.FIRST_VALID:
                page.icon = { name: 'intro-flow-location-valid', animate: false };
                page.state = { actions: [Action.NEXT], loadingMessage: $localize`Found it!`, disableButtons: false };
                break;
            default: break;
        }
    }
}

export class DirectionPage extends Page {
    public readonly key = PageKey.directionPage;
    public header = $localize`Allow Direction`;
    public icon = { name: 'intro-flow-direction', animate: false };
    public description = $localize`To make navigation easier, we recommend that you enable your direction services.`;
    public state = { actions: [Action.ALLOW_DIRECTION], loadingMessage: '', disableButtons: false };

    constructor(private gyroService: GyroService) {
        super();
    }

    public setCookie() { return null }

    public async show(tour: Mote) {
        if(!tour) return false;
        // Show if tour is specified and gyro has not been initialized
        return tour.project != MotesPaths.DEFAULT_PROJECT_KEY && !(this.gyroService.permissionsInitialized);
    }

    public updatePage() { return null }
}

export class GroupPresentationPage extends Page {
    public readonly key = PageKey.groupPresentationPage;
    public header = $localize`With friends?`;
    public icon = { name: 'intro-flow-group', animate: false };
    public description = $localize`Do you want to embark on this adventure with your friends? You can always join a group later.`;
    public state = { actions: [Action.NO_GROUP, Action.GROUP_TOUR], loadingMessage: '', disableButtons: false };

    constructor(private introFlowService: IntroFlowService) {
        super();
    }

    public async setCookie(tour: Mote) {
        return this.introFlowService.setLocalStorage(MotesPaths.LOCAL_STORAGE_GROUP_PRES_SEEN(tour.key))
    }

    public async show(tour: Mote, user: User) {
        if(!tour) return false;
        // Show when tour has been started and user isn't in a group and hasn't seen this page before
        return (tour.project != MotesPaths.DEFAULT_PROJECT_KEY) && !(await this.introFlowService.isLocalStorageSet(MotesPaths.LOCAL_STORAGE_GROUP_PRES_SEEN(tour.key))) && !user?.groupKey;
    }

    public updatePage() { return null };
}

export class GroupPage extends Page {
    public readonly key = PageKey.groupPage;
    public state = { actions: [Action.CONTINUE], loadingMessage: '', disableButtons: false };
    public groupPage = true;

    constructor(private introFlowService: IntroFlowService) {
        super();
    }

    public setCookie() { return null }

    public async show(tour: Mote, user: User) {
        if(!tour) return false;
        // Show when tour has been started and user isn't in a group and hasn't seen the group presentation before
        return (tour.project != MotesPaths.DEFAULT_PROJECT_KEY) && !(await this.introFlowService.isLocalStorageSet(MotesPaths.LOCAL_STORAGE_GROUP_PRES_SEEN(tour.key))) && !user?.groupKey;
    }

    public async updatePage(page: Page, tour: Mote, user: User) {
        page.header = user?.groupKey ? $localize`My group` : $localize`Group`;
        page.description = user?.groupKey ? $localize`You and your friends can join, leave, and create groups at any time from your profile.` : $localize`Create a group or join an existing group by inserting the Group ID.`;
    }
}

export class TrailerPage extends Page {
    public readonly key = PageKey.trailerPage;
    public description = $localize`Are you ready to unlock the stories around you?`;
    public state = { actions: [Action.START_TOUR], loadingMessage: '', disableButtons: false };
    public mediaType = MediaType.VIDEO;

    constructor(private introFlowService: IntroFlowService) {
        super();
    }

    public async setCookie(tour: Mote) {
        this.introFlowService.setLocalStorage(MotesPaths.LOCAL_STORAGE_TRAILER_SEEN(tour.key))
    }

    public async show(tour: Mote) {
        if(!tour) return false;
        // Show if tour is specified, contains a trailer and user hasn't seen this page before
        return tour.project != MotesPaths.DEFAULT_PROJECT_KEY && tour.image?.video?.url && !(await this.introFlowService.isLocalStorageSet(MotesPaths.LOCAL_STORAGE_TRAILER_SEEN(tour.key)));
    }

    public async updatePage(page: Page, tour: Mote) {
        page.header = tour.project == MotesPaths.DEFAULT_PROJECT_KEY ? $localize`What is StoryHunt?` : $localize`Start tour`;
        page.state.actions = tour.project == MotesPaths.DEFAULT_PROJECT_KEY ? [Action.EXPLORE_TOURS] : [Action.START_TOUR];
        page.media = tour?.image;
    }
}

export class MapUserPage extends Page {
    public readonly key = PageKey.mapUserPage;
    public header = $localize`Your position`;
    public description = $localize`The small blue dot is you. The large blue circle is your unlock radius.`;
    public state = { actions: [Action.MAP_USER_SEEN], loadingMessage: '', disableButtons: false };
    public mapPage = true;

    constructor(private introFlowService: IntroFlowService, private mapService: MapService, private positionService: PositionService, private moteEventService: MoteEventService) {
        super();
    }

    public setCookie() { return null }

    public async show(tour: Mote) {
        if(!tour) return false;
        // Show if tour is specified, user pos is found, and user hasn't seen the final map page for this tour
        const pos = await raceTimeout(this.positionService.getPosition());
        const userPosFound = pos && pos.isValid;
        // Set map position to center of tour if there is no user to zoom map from (will zoom from tour center to first mote in next view)
        if(!userPosFound) this.mapService.setCenter(tour.position);
        return tour.project != MotesPaths.DEFAULT_PROJECT_KEY && userPosFound && !(await this.introFlowService.isLocalStorageSet(MotesPaths.LOCAL_STORAGE_MAP_INTRO_SEEN(tour.key)));
    }

    public async updatePage(page: Page, tour: Mote) {
        // Open map behind intro flow
        this.moteEventService.emitMoteEvent({ mote: tour, type: MoteEventType.SHOW_MAP });
        // Hide tour and fly to user
        this.mapService.hideTour();
        const pos = await raceTimeout(this.positionService.getPosition());
        if(pos) this.mapService.flyTo(pos, 16);
    }
}

export class MapStoryPage extends Page {
    public readonly key = PageKey.mapStoryPage;
    public state = { actions: [Action.MAP_STORY_SEEN], loadingMessage: '', disableButtons: false };
    public mapPage = true;

    constructor(private introFlowService: IntroFlowService, private mapService: MapService, private moteEventService: MoteEventService) {
        super();
    }

    public async setCookie(tour: Mote) {
        return this.introFlowService.setLocalStorage(MotesPaths.LOCAL_STORAGE_MAP_INTRO_SEEN(tour.key));
    }

    public async show(tour: Mote) {
        if(!tour) return false;
        // Show if tour is specified and user hasn't seen the final map page for this tour
        return tour.project != MotesPaths.DEFAULT_PROJECT_KEY && !(await this.introFlowService.isLocalStorageSet(MotesPaths.LOCAL_STORAGE_MAP_INTRO_SEEN(tour.key)));
    }

    public async updatePage(page: Page, tour: Mote, user: User, gpsState: GpsState, activeIcon: string, gpsHelpComplete: boolean, firstMote: Mote) {
        // Open map behind intro flow (if not already)
        this.moteEventService.emitMoteEvent({ mote: tour, type: MoteEventType.SHOW_MAP });

        // Set header and description according to tour type and mote sort
        let storyPosition = getStoryPosition(tour);
        let storyType = getStoryType(tour);

        page.header = firstMote ? `${capitalize(storyPosition)} ${storyType}` : $localize`The tour`;
        page.description = $localize`To unlock the ${storyPosition} ${storyType}, you must move closer until it's within your unlock radius`;
        // Show tour and fly to first/closets mote
        this.mapService.showTour();
        this.mapService.hideDirections();
        // Fly to first mote if is has been found at the time of showing this page
        if(firstMote) {
            // Fly to first mote
            this.moteEventService.emitMoteEvent({ mote: firstMote, type: MoteEventType.SHOW_MOTE_ON_MAP });
            this.moteEventService.emitMoteEvent({ type: MoteEventType.MAP_MOTE_SELECTED, mote: firstMote, options: { preventOpenStoryModal: true } });
            return;
        }
        // Otherwise fly to tour
        this.moteEventService.emitMoteEvent({ mote: tour, type: MoteEventType.FLY_TO_TOUR, options: { padding: { top: 50, bottom: 50, left: 20, right: 20 } } });
    }
}

export class MapNavigationPage extends Page {
    public readonly key = PageKey.mapNavigationPage;
    public header = $localize`Getting there`;
    public state = { actions: [Action.MAP_NAV_SEEN], loadingMessage: '', disableButtons: false };
    public mapPage = true;

    constructor(private introFlowService: IntroFlowService, private mapService: MapService, private positionService: PositionService, private moteEventService: MoteEventService) {
        super();
    }

    public async setCookie(tour: Mote) {
        return this.introFlowService.setLocalStorage(MotesPaths.LOCAL_STORAGE_MAP_INTRO_SEEN(tour.key));
    }

    public async show(tour: Mote, user: User, tourAccess: boolean) {
        if(!tour) return false;
        // Show if tour is specified, user pos is found, and user hasn't seen the final map page for this tour
        const pos = await raceTimeout(this.positionService.getPosition());
        const userPosFound = pos && pos.isValid;
        return tour.project != MotesPaths.DEFAULT_PROJECT_KEY && userPosFound && !(await this.introFlowService.isLocalStorageSet(MotesPaths.LOCAL_STORAGE_MAP_INTRO_SEEN(tour.key)));
    }

    public async updatePage(page: Page, tour: Mote, user: User, gpsState: GpsState, activeIcon: string, gpsHelpComplete: boolean, firstMote: Mote) {
        // Open map behind intro flow (if not already)
        this.moteEventService.emitMoteEvent({ mote: tour, type: MoteEventType.SHOW_MAP });
        
        // Set description according to tour type and mote sorting
        let storyPosition = getStoryPosition(tour);
        let storyType = getStoryType(tour);

        page.description = $localize`This is how you get to the ${storyPosition} ${storyType} on your tour. Are you ready?`;

        // Navigate to first mote
        const mapBannerPadding = { top: 200, left: 40, right: 40, bottom: 200 };
        this.mapService.showDirections();
        this.moteEventService.emitMoteEvent({ mote: firstMote, type: MoteEventType.NAVIGATE_TO_MOTE, options: { preventOpenStoryModal: true, preventZoom: false, padding: mapBannerPadding } });

        // Fly to first mote and user position (if not lost)
        let positions = [firstMote.position.longLat()];
        const userPos = await raceTimeout(this.positionService.getPosition());
        if(userPos && userPos.isValid) positions.push(userPos.longLat());
        this.mapService.fitMapBoundsToCoords(positions, mapBannerPadding, 1.5, true);
    }
}

export class TutorialPage extends Page {
    public readonly key = PageKey.tutorialPage;
    public animationPage = true;
    public state = { actions: [Action.START_TUTORIAL], loadingMessage: '', disableButtons: false };
    public description = $localize`Are you ready to unlock the stories around you?`;

    constructor(private introFlowService: IntroFlowService) {
        super();
    }

    public setCookie() { return null }

    public async show(tour: Mote) {
        // Show if tour is not specified and this is first intro flow
        return (!tour || tour.project == MotesPaths.DEFAULT_PROJECT_KEY) && !(await this.introFlowService.isLocalStorageSet(MotesPaths.LOCAL_STORAGE_FLOW_COMPLETED()));
    }

    public async updatePage(page: Page, tour: Mote, user: User, gpsState: GpsState, activeIcon: string, gpsHelpComplete: boolean, firstMote: Mote, tourAccess: boolean, updateState: boolean) {
        // Check if state should automatically be updated (because of on-page click) or not (because of page change)
        if(!updateState) return;
        // Set state depending on current state
        switch(page.state.actions[0]) {
            case Action.START_TUTORIAL:
                page.state = { actions: [Action.USER_ANIMATION_SEEN], loadingMessage: '', disableButtons: false };
                page.description = $localize`The blue dot represents your position.`;
                break;
            case Action.USER_ANIMATION_SEEN:
                page.state = { actions: [Action.STORY_ANIMATION_SEEN], loadingMessage: '', disableButtons: false };
                page.description = $localize`This is a story, but it's locked. It must be within your radius to be unlocked.`;
                break;
            case Action.STORY_ANIMATION_SEEN:
                page.state = { actions: [Action.UNLOCK_ANIMATION_SEEN], loadingMessage: '', disableButtons: false };
                page.description = $localize`Perfect! Now you can access the story.`;
                break;
            case Action.UNLOCK_ANIMATION_SEEN:
                page.state = { actions: [Action.TOUR_ANIMATION_SEEN], loadingMessage: '', disableButtons: false };
                page.description = $localize`Stories are a part of tours. You know, like guided tours.`;
                break;
            case Action.TOUR_ANIMATION_SEEN:
                page.state = { actions: [Action.ROUTE_ANIMATION_SEEN], loadingMessage: '', disableButtons: false };
                page.description = $localize`Tours can be linear with a start and an end.`;
                break;
            case Action.ROUTE_ANIMATION_SEEN:
                page.state = { actions: [Action.COLLECTION_ANIMATION_SEEN], loadingMessage: '', disableButtons: false };
                page.description = $localize`Or they can be non-linear with no defined start and end - free for you to explore!`;
                break;
            case Action.COLLECTION_ANIMATION_SEEN:
                page.state = { actions: [Action.BROWSE_TOURS], loadingMessage: '', disableButtons: false };
                page.description = $localize`Whether you are into ghost stories, historical sagas or something else, we have a tour for you!`;
                break;
            default:
                page.state = { actions: [Action.START_TUTORIAL], loadingMessage: '', disableButtons: false };
                page.description = $localize`Are you ready to unlock the stories around you?`;
                break;
        }
    };
}
